import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { AlertTypes } from '../constants';
import { onColor, FONT_MONO, drillName } from '../constants/tokens';

/**
 * Format seconds as mm:ss, padding both parts so the banner does not jitter as
 * the timer advances.
 *
 * @param {number} totalSeconds
 * @return {string}
 */
const formatElapsed = (totalSeconds) => {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return minutes + ':' + seconds;
};

/**
 * The full-bleed banner shown while the school is in an alert state.
 *
 * Before v7 this role belonged to the AppBar, which painted its whole
 * background with the status color. Moving it here lets the header stay a
 * stable, legible piece of chrome while the alert itself gets the full width
 * and a much louder treatment.
 *
 * Foreground colors are derived from the status hex via onColor() rather than
 * hardcoded: the status set runs from yellow to purple, so no single ink works
 * across all of them.
 */
function AlertBanner({ schoolStatus, isDrill, isAuthenticated }) {
  const history = useHistory();
  const status = schoolStatus || AlertTypes.UNKNOWN;

  const inAlert =
    isAuthenticated &&
    status !== AlertTypes.NORMAL &&
    status !== AlertTypes.UNKNOWN;

  // Start of the current alert. Reset whenever the status changes, so the
  // timer measures this alert rather than the session.
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  const statusName = status.getDashboardStatus();

  useEffect(() => {
    setStartedAt(Date.now());
    setNow(Date.now());
  }, [statusName]);

  useEffect(() => {
    if (!inAlert) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [inAlert]);

  if (!inAlert) return null;

  const background = status.getColor().main;
  const ink = onColor(background);
  const soft = onColor(background, true);
  const elapsed = formatElapsed(Math.max(0, Math.floor((now - startedAt) / 1000)));

  return (
    <div style={{ background: background, color: ink }}>
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '16px 24px',
          padding: '20px 32px'
        }}
      >
        <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: 9999, flex: 'none', background: ink }} />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '16px',
                textTransform: 'uppercase',
                color: soft
              }}
            >
              {isDrill ? 'School status · drill' : 'School status'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '28px', color: ink }}>
                {isDrill ? drillName(statusName) : statusName}
              </span>
              {/* A drill must never be mistaken for the real thing, or the
                  real thing for a drill, so it is said twice, plainly. */}
              {isDrill &&
                <span
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '16px',
                    textTransform: 'uppercase',
                    padding: '4px 10px',
                    borderRadius: 9999,
                    border: '1px solid ' + ink,
                    color: ink
                  }}
                >
                  Drill · no texts or 911 call
                </span>}
            </div>
          </div>
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '16px',
              textTransform: 'uppercase',
              color: soft
            }}
          >
            Elapsed
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 24, fontWeight: 500, lineHeight: '32px', color: ink }}>
            {elapsed}
          </div>
        </div>

        <div style={{ flex: '0 0 auto', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <button
            type='button'
            onClick={() => history.push('/dashboard/map')}
            style={{
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 40,
              padding: '0 20px',
              borderRadius: 6,
              border: 0,
              background: ink,
              color: background,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            View live map
          </button>
        </div>
      </div>
    </div>
  );
}

AlertBanner.propTypes = {
  schoolStatus: PropTypes.object,
  isDrill: PropTypes.bool,
  isAuthenticated: PropTypes.bool
};

const mapStateToProps = (state) => ({
  schoolStatus: state.status.schoolStatus,
  isDrill: state.status.isDrill === true,
  isAuthenticated: state.auth.isAuthenticated
});

export default connect(mapStateToProps)(AlertBanner);
